// lib/koi-pond-v3/postprocess/colorGrade.ts
//
// T9.3 — combined color-grade + vignette ShaderPass (one pass instead of
// two — they're both cheap full-screen ops over the same buffer).
//
// Final pass in the composer (no OutputPass — see PondSceneV3 for why),
// so it operates on sRGB-encoded values. Good for a stylistic split-tone:
// the luma thresholds land where the eye expects them (moon ≈ 1, glints
// ≈ 0.3–0.6, dark water ≈ 0.05).
//
//   - Teal-and-orange grade: shadows pushed toward teal, highlights toward
//     warm cream. Restrained — blended back over the source at
//     uGradeStrength. On this scene it gives the dark water a faint teal
//     cast and warms the moon + glints.
//   - Vignette: corners fall to ~(1 - uVignette) of centre brightness with
//     a smooth gradient.

export const ColorGradeVignetteShader = {
  uniforms: {
    tDiffuse: { value: null as unknown },
    uGradeStrength: { value: 0.6 },
    uVignette: { value: 0.22 }, // corners → ~78% of centre (let moonlight reach the foreground)
    uFlash: { value: 0 },       // dragon-gate detonation flash (rest 0 = no-op)
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uGradeStrength;
    uniform float uVignette;
    uniform float uFlash;
    varying vec2 vUv;
    void main() {
      vec4 src = texture2D(tDiffuse, vUv);
      vec3 col = src.rgb;

      // teal-and-orange split-tone by luma
      float luma = dot(col, vec3(0.299, 0.587, 0.114));
      vec3 shadowTint = vec3(0.82, 0.97, 1.08); // teal
      vec3 highTint = vec3(1.08, 1.0, 0.88);    // warm cream
      vec3 tint = mix(shadowTint, highTint, smoothstep(0.02, 0.5, luma));
      col = mix(col, col * tint, uGradeStrength);

      // vignette — smooth corner falloff
      vec2 d = vUv - 0.5;
      float r = length(d) * 1.414; // ~1.0 at the corners
      float vig = 1.0 - uVignette * smoothstep(0.45, 1.0, r);
      col *= vig;

      // dragon-gate warm detonation flash (rest uFlash=0 → no change)
      col += vec3(1.0, 0.92, 0.78) * (uFlash * 0.35);

      gl_FragColor = vec4(col, src.a);
    }
  `,
};
